import { uniq } from 'Utility/ramdaReplacement';
import { fetchWithOptions } from '../../utility/fetch';
import { parseJWTFromCookie } from '../../utility/jwtAuthTools';
import { ActionableErrorContext, Loggable, makeLog } from '../../utility/logger';
import { ICompactVaultUser, INeuroVisit } from 'neuro-data-structures';
import { uniqBy } from 'ramda';
import { PatientCountryCode } from 'neuro-constants';

/**
 * Sync patient medications from med integrations api
 * @returns {boolean} Whether sync was successful
 */
export const medicationSync = async (system: 'miranda' | 'apotti' | 'lifecare'): Promise<boolean> => {
  const syncUrlMap: { [key in typeof system]: string } = {
    miranda: '/api/requestMirandaUpdate',
    apotti: '/api/apottiMedication/v1/',
    lifecare: '/api/v1/lifecare/medication',
  };

  return await fetchWithOptions(syncUrlMap[system], { neurojwt: parseJWTFromCookie() }, { method: 'get' })
    .then(res => {
      if (res.status === 200) {
        return true;
      }
      throw new Error('unexpected status ' + res.status);
    })
    .catch(err => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'medication sync ' + system));
      return false;
    });
};

/**
 * Get care table data from Uranus
 * @returns {boolean} Whether sync was successful
 */
export const caretableSync = async (): Promise<boolean> => {
  return await fetchWithOptions(`/api/v1/caretable/uranus`, { neurojwt: parseJWTFromCookie() }, { method: 'get' })
    .then(res => {
      if (res.status === 200) {
        return true;
      }
      throw new Error('unexpected status ' + res.status);
    })
    .catch(err => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'Care table sync'));
      return false;
    });
};

/**
 * Get laboratory data from lifecare
 * @returns {boolean} Whether sync was successful
 */
export const lifecareLaboratorySync = async (): Promise<boolean> => {
  return await fetchWithOptions(`/api/v1/lifecare/laboratory`, { neurojwt: parseJWTFromCookie() }, { method: 'get' })
    .then(res => {
      if (res.status === 200) {
        return true;
      }
      throw new Error('unexpected status ' + res.status);
    })
    .catch(err => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'Lifecare laboratory sync'));
      return false;
    });
};

/**
 * Trigger synchronization of Fimlab lab data for currently active patient
 * @returns {Promise<boolean>} Resolve with success status
 */
export const fimlabSync = async (): Promise<boolean> => {
  try {
    const response = await fetchWithOptions(
      '/api/fimlabsync/v1/',
      { neurojwt: parseJWTFromCookie() },
      { method: 'get' },
    );

    if (response.status !== 200) {
      throw new Error('unexpected status ' + response.status);
    }

    return true;
  } catch (error) {
    makeLog(
      Loggable.BugActionable,
      error instanceof Error ? error : new Error('strange error'),
      new ActionableErrorContext(null, 'fimlab sync'),
    );
    return false;
  }
};

/**
 * Get imaging examination data from L-Force
 * @returns {boolean} Whether sync was successful
 */
export const imagingSync = async (): Promise<boolean> => {
  const searchDate = '2020-01-01'; // The date must be formatted as yyyy-mm-dd.

  return await fetchWithOptions(
    `/api/v1/lforce/examination/${searchDate}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'get' },
  )
    .then(res => {
      if (res.status === 200) {
        return true;
      }
      throw new Error('unexpected status ' + res.status);
    })
    .catch(err => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'imaging sync'));
      return false;
    });
};

/**
 * Fetch organization data based on JWT
 * @returns {Promise<TVaultOrganization | null>} Org data or null
 */
export const fetchOrganizationData = async (): Promise<TVaultOrganization | null> => {
  const neurojwt = parseJWTFromCookie();
  if (!neurojwt) return null;
  try {
    const response = await fetchWithOptions(
      '/api/vault/v2/organizations/',
      { neurojwt, requestorigin: 'neuro-ui' },
      { method: 'GET' },
    );
    if (response.status === 200) {
      return response.json();
    }
    return null;
  } catch (error) {
    makeLog(
      Loggable.BugActionable,
      error instanceof Error ? error : new Error('strange error'),
      new ActionableErrorContext(null, 'organization data fetch'),
    );
    return null;
  }
};

/**
 * Fetch all organizations in the current group
 * @param {string} groupName - Group name
 * @returns {Promise<Array<Pick<TVaultOrganization, 'orgId'|'displayName'>>|null>} Org data array or null
 */
export const fetchOrganizationGroup = async (
  groupName: string,
): Promise<Array<Pick<TVaultOrganization, 'orgId' | 'displayName'>> | null> => {
  const neurojwt = parseJWTFromCookie();
  if (!neurojwt) return null;
  try {
    const response = await fetchWithOptions(
      `/api/vault/v2/organizations?groupName=${groupName}`,
      { neurojwt, requestorigin: 'neuro-ui' },
      { method: 'GET' },
    );
    if (response.status === 200) {
      return response.json();
    }
    return null;
  } catch (error) {
    makeLog(
      Loggable.BugActionable,
      error instanceof Error ? error : new Error('strange error'),
      new ActionableErrorContext(null, 'organization group fetch'),
    );
    return null;
  }
};

/**
 * Fetch preferred platforms for given patient
 * @returns {Promise<Array<Platform> | null>} Platforms array or null
 */
export const fetchPreferredPlatforms = async (): Promise<Array<Platform> | null> => {
  return fetchWithOptions(
    '/api/recommend/platform',
    { neurojwt: parseJWTFromCookie() },
    {
      method: 'GET',
    },
  )
    .then(res => {
      if (res.status === 200) {
        return res.json();
      } else throw new Error('unexpected status ' + res.status);
    })
    .then(data => data)
    .catch((err: Error) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'preferred platforms fetch'));
      return null;
    });
};

// Returns users for the given organization
export const fetchOrgUsers = async (orgid: string): Promise<Array<ICompactVaultUser> | null> => {
  return fetchWithOptions(
    '/api/vault/v2/userless/' + orgid,
    { neurojwt: parseJWTFromCookie() },
    {
      method: 'GET',
    },
  )
    .then(res => {
      if (res.status === 200) {
        return res.json();
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((err: Error) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'fetch users'));
      return null;
    });
};

// Get statistics token for platform
export const getStatisticsTokenForPlatform = async (platform: string): Promise<ITokenData | null> => {
  return fetchWithOptions(`/api/powerbi/${platform}`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then(res => {
      if (res.ok) return res.json();
      else throw new Error('unexpected status ' + res.status);
    })
    .catch(error => {
      makeLog(Loggable.BugActionable, error, new ActionableErrorContext(null, 'Power BI token fetch'));
      return null;
    });
};

/**
 * Register to answer / fetch surveys
 * @returns {any[]} Return a list of surveys
 */
export const surveysRegister = async (): Promise<IUserSurvey | null> => {
  return await fetchWithOptions(
    `/api/analytics/survey/register`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST' },
  )
    .then(res => {
      if (res.ok) {
        if (res.status === 204) return null;
        else return res.json();
      } else {
        throw new Error('unexpected status ' + res.status);
      }
    })
    .then((res: IUserSurvey | null) => {
      if (res?.survey_id) return res;
      else return null;
    })
    .catch(err => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'survey register'));
      return null;
    });
};

/**
 * Answer a survey
 * @returns {boolean} Whether it was successful
 */
export const surveysAnswer = async (surveyid: string, surveyData: IUserSurveyAnswers): Promise<boolean> => {
  return await fetchWithOptions(
    `/api/analytics/survey/answer/${surveyid}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: JSON.stringify({ answers: { ...surveyData } }) },
  )
    .then(res => {
      if (res.ok) {
        return true;
      }
      throw new Error('unexpected status ' + res.status);
    })
    .catch(err => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'answer survey'));
      return false;
    });
};

/**
 * Dismiss a survey
 * @returns {boolean} Whether it was successful
 */
export const surveysDismiss = async (surveyid: string): Promise<boolean> => {
  return await fetchWithOptions(
    `/api/analytics/survey/dismiss/${surveyid}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST' },
  )
    .then(res => {
      if (res.ok) {
        return true;
      }
      throw new Error('unexpected status ' + res.status);
    })
    .catch(err => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'survey dismiss'));
      return false;
    });
};

export interface IPatientNames {
  timestamp: number;
  id: string;
  firstNames: string;
  lastNames: string;
  ssn: string;
  ssnLocalization: PatientCountryCode;
}

/**
 * Get patients data list from an id list
 * @returns {Promise<IPatientNames[]>|false} List or false
 */
export const patientDataList = async (patientIds: string[], source: string): Promise<IPatientNames[] | false> => {
  return await fetchWithOptions(
    `api/patient/v2/namessnlist`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'POST', body: JSON.stringify(patientIds) },
  )
    .then(res => {
      if (res.ok) {
        return res.json();
      }
      throw new Error('unexpected status ' + res.status);
    })
    .catch(err => {
      makeLog(
        Loggable.BugActionable,
        err,
        new ActionableErrorContext(null, 'names ssn list fetch initiated by ' + source),
      );
      return false;
    });
};

/**
 * Get visited patients
 * @returns {INeuroPersonData[]|false} Patient or false
 */
export const getVisitedPatients = async (): Promise<Array<IPatientNames> | false> => {
  return await fetchWithOptions(`/api/visit/user`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then(res => {
      if (res.ok) {
        return res.json();
      }
      throw new Error('unexpected status ' + res.status);
    })
    .then(async (visitArray: Array<INeuroVisit & { patientId: string }>) => {
      const timestampsWithIDs = uniqBy(
        s => s.patientId,
        visitArray.toSorted((v1, v2) => v2.createDate - v1.createDate),
      );
      const patientIds = uniq(visitArray.map(vs => vs.patientId));
      return patientDataList(patientIds, 'visited patient listing').then(
        (json: Array<Omit<IPatientNames, 'timestamp'>> | false) => {
          if (!json) return false;
          return timestampsWithIDs.map(times => {
            const found = json.find(json => json.id === times.patientId)!;
            return { ...found, timestamp: times.createDate };
          });
        },
      );
    })
    .catch(err => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'GET visits'));
      return false;
    });
};

/**
 * Specific medication - search
 * @param {string} currentSearchString - Medication name or part of it
 * @returns {Promise<Array<Pick<TVaultOrganization, 'orgId'|'displayName'>>>} Org data array
 */
export const medicationSearchGet = (currentSearchString: string): Promise<IMedicationSearch[]> =>
  fetchWithOptions(
    `/api/medication/search?search=${currentSearchString}`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error('Status not 200 ' + res.status);
      }
    })
    .catch(err => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'Medication search'));
      return null;
    });
